import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle,
  CreditCard,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Send,
  Loader2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import FormInput from './FormInput';
import ThemeToggle from './ThemeToggle';
import { toast } from '../utils/toast';
import { generateCreditCardPdf } from '../utils/generateCreditCardPdf';
import type { PublicInviteData, CreditCardAuthorizationFormData, AuditLogEntry, Invite } from '../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const CARD_TYPES = [
  { value: 'visa', label: 'VISA' },
  { value: 'mastercard', label: 'MASTERCARD' },
  { value: 'amex', label: 'AMERICAN EXPRESS' },
  { value: 'discover', label: 'DISCOVER' },
  { value: 'debit', label: 'DEBIT CARD' },
] as const;

type CardType = 'visa' | 'mastercard' | 'amex' | 'discover' | 'debit';

type FormState = {
  card: {
    cardHolderFirstName: string;
    cardHolderLastName: string;
    companyName: string;
    cardType: CardType | '';
    cardNumber: string;
    expMonth: string;
    expYear: string;
    cvv: string;
    telephone: string;
    billingAddress: string;
    city: string;
    state: string;
    zipCode: string;
    invoiceOrderNumber: string;
    totalAmount: string;
  };
  authorization: {
    validUntilMonth: string;
    validUntilYear: string;
    initials: string;
    policyAccepted: boolean;
    signature: string;
    signedDate: string;
  };
};

function renderSignatureForPdf(sig: string): React.ReactNode {
  if (!sig) return null;
  if (sig.startsWith('TYPED:')) {
    return (
      <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '18px', color: '#1e293b' }}>
        {sig.replace('TYPED:', '')}
      </span>
    );
  }
  return (
    <img
      src={sig}
      alt="Authorized Signature"
      style={{ maxWidth: '280px', height: '60px', objectFit: 'contain' }}
    />
  );
}

interface CreditCardAuthorizationFormProps {
  invite: PublicInviteData;
  onSubmitted?: () => void;
}

export default function CreditCardAuthorizationForm({ invite, onSubmitted }: CreditCardAuthorizationFormProps) {
  const [loading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(invite.status === 'completed');
  const [completedAppId, setCompletedAppId] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [error] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [ip, setIp] = useState('Unknown');
  const [openedAt] = useState(new Date());
  const pdfRef = useRef<HTMLDivElement>(null);

  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState<FormState>({
    card: {
      cardHolderFirstName: invite.signerName?.split(' ')[0] || '',
      cardHolderLastName: invite.signerName?.split(' ').slice(1).join(' ') || '',
      companyName: invite.companyName || '',
      cardType: '',
      cardNumber: '',
      expMonth: '',
      expYear: '',
      cvv: '',
      telephone: '',
      billingAddress: '',
      city: '',
      state: '',
      zipCode: '',
      invoiceOrderNumber: '',
      totalAmount: '',
    },
    authorization: {
      validUntilMonth: '',
      validUntilYear: '',
      initials: '',
      policyAccepted: false,
      signature: '',
      signedDate: today,
    },
  });

  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const text = await res.text();
        const data = (text && !text.trim().startsWith('<')) ? JSON.parse(text) : {};
        setIp(data.ip || 'Unknown');
      } catch {
        // Non-critical — IP used for audit log only
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateStep(step, true);
    }
  }, [formData, step]);

  const validateStep = (currentStep: number, isRealTime = false): boolean => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!formData.card.cardHolderFirstName) newErrors.cardHolderFirstName = 'First name is required';
      if (!formData.card.cardHolderLastName) newErrors.cardHolderLastName = 'Last name is required';
      if (!formData.card.companyName) newErrors.companyName = 'Company name is required';
      if (!formData.card.cardType) newErrors.cardType = 'Card type is required';
      if (!formData.card.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!/^\d{13,19}$/.test(formData.card.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Enter a valid card number (13–19 digits)';
      }
      if (!formData.card.expMonth) {
        newErrors.expMonth = 'Required';
      } else if (!/^(0?[1-9]|1[0-2])$/.test(formData.card.expMonth)) {
        newErrors.expMonth = '01–12';
      }
      if (!formData.card.expYear) {
        newErrors.expYear = 'Required';
      } else if (!/^\d{4}$/.test(formData.card.expYear) || parseInt(formData.card.expYear) < new Date().getFullYear()) {
        newErrors.expYear = 'Invalid year';
      }
      if (!formData.card.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (!/^\d{3,4}$/.test(formData.card.cvv)) {
        newErrors.cvv = '3 or 4 digits';
      }
      if (!formData.card.telephone) newErrors.telephone = 'Telephone is required';
      if (!formData.card.billingAddress) newErrors.billingAddress = 'Billing address is required';
      if (!formData.card.city) newErrors.city = 'City is required';
      if (!formData.card.state) newErrors.state = 'State is required';
      if (!formData.card.zipCode) {
        newErrors.zipCode = 'ZIP is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.card.zipCode)) {
        newErrors.zipCode = 'Invalid ZIP';
      }
    }

    if (currentStep === 2) {
      if (!formData.authorization.validUntilMonth) {
        newErrors.validUntilMonth = 'Required';
      } else if (!/^(0?[1-9]|1[0-2])$/.test(formData.authorization.validUntilMonth)) {
        newErrors.validUntilMonth = '01–12';
      }
      if (!formData.authorization.validUntilYear) {
        newErrors.validUntilYear = 'Required';
      } else if (!/^\d{4}$/.test(formData.authorization.validUntilYear)) {
        newErrors.validUntilYear = 'YYYY';
      }
      if (!formData.authorization.initials) newErrors.initials = 'Initials are required';
      if (!formData.authorization.policyAccepted) newErrors.policyAccepted = 'You must agree to the authorization terms';
      if (!formData.authorization.signature) newErrors.signature = 'Authorized signature is required';
    }

    const filteredErrors = isRealTime
      ? Object.keys(newErrors).reduce((acc, key) => {
          if (touched[key]) acc[key] = newErrors[key];
          return acc;
        }, {} as Record<string, string>)
      : newErrors;

    setErrors(filteredErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    const fields = ['cardHolderFirstName', 'cardHolderLastName', 'companyName', 'cardType',
      'cardNumber', 'expMonth', 'expYear', 'cvv', 'telephone', 'billingAddress', 'city', 'state', 'zipCode'];
    setTouched(prev => ({ ...prev, ...Object.fromEntries(fields.map(f => [f, true])) }));
    if (validateStep(1)) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    setErrors({});
    setTouched({});
    setStep(1);
    window.scrollTo(0, 0);
  };

  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // We completely replace buildPdf with generateCreditCardPdf below, so buildPdf can be removed.

  const handleSubmit = async () => {
    if (!invite?.id) return;
    const authFields = ['validUntilMonth', 'validUntilYear', 'initials', 'policyAccepted', 'signature'];
    setTouched(prev => ({ ...prev, ...Object.fromEntries(authFields.map(f => [f, true])) }));
    if (!validateStep(2)) return;
    setSubmitting(true);

    try {
      const userAgent = navigator.userAgent;

      const auditLog = [
        {
          action: 'Invite Created',
          timestamp: typeof invite.createdAt === 'string' ? invite.createdAt : new Date().toISOString(),
          details: `Invitation sent to ${invite.customerEmail} for ${invite.companyName}`,
          ip: 'System',
          userAgent: 'System',
        },
        {
          action: 'Application Opened',
          timestamp: openedAt.toISOString(),
          details: 'Authorization link accessed by customer',
          ip,
          userAgent,
        },
        {
          action: 'Card Information Entered',
          timestamp: new Date().toISOString(),
          details: `Card type: ${formData.card.cardType?.toUpperCase()}, last 4: ${formData.card.cardNumber.slice(-4)}`,
          ip,
          userAgent,
        },
        {
          action: 'Authorization Terms Accepted',
          timestamp: new Date().toISOString(),
          details: `Authorization valid until ${formData.authorization.validUntilMonth}/${formData.authorization.validUntilYear}`,
          ip,
          userAgent,
        },
        {
          action: 'Signature Captured',
          timestamp: new Date().toISOString(),
          details: 'Customer applied authorized signature',
          ip,
          userAgent,
        },
        {
          action: 'Form Submitted',
          timestamp: new Date().toISOString(),
          details: `Credit card authorization submitted for ${invite.companyName}`,
          ip,
          userAgent,
        },
      ];

      const submittedFormData: CreditCardAuthorizationFormData = {
        formType: 'credit_card_authorization',
        card: {
          cardHolderFirstName: formData.card.cardHolderFirstName,
          cardHolderLastName: formData.card.cardHolderLastName,
          companyName: formData.card.companyName,
          cardType: formData.card.cardType as CardType,
          cardNumber: formData.card.cardNumber.replace(/\s/g, ''),
          expMonth: formData.card.expMonth,
          expYear: formData.card.expYear,
          cvv: formData.card.cvv,
          telephone: formData.card.telephone,
          billingAddress: formData.card.billingAddress,
          city: formData.card.city,
          state: formData.card.state,
          zipCode: formData.card.zipCode,
          invoiceOrderNumber: formData.card.invoiceOrderNumber,
          totalAmount: formData.card.totalAmount,
        },
        authorization: {
          validUntilMonth: formData.authorization.validUntilMonth,
          validUntilYear: formData.authorization.validUntilYear,
          initials: formData.authorization.initials,
          policyAccepted: formData.authorization.policyAccepted,
          signature: formData.authorization.signature,
          signedDate: formData.authorization.signedDate,
        },
      };

      let pdfResult: { blobUrl: string, base64: string } | null = null;
      try {
        const b64 = await generateCreditCardPdf(
          submittedFormData,
          invite,
          invite.id,
          auditLog
        );
        if (b64) {
          const byteString = atob(b64);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          pdfResult = { blobUrl, base64: b64 };
          setPdfBlobUrl(blobUrl);
        }
      } catch (err) {
        console.error('PDF generation failed', err);
      }

      // Submit to CRM API
      const response = await fetch(`/api/public/forms/${invite.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: invite.companyName,
          customerEmail: invite.customerEmail,
          formData: submittedFormData,
          signedPdfBase64: pdfResult?.base64 ? `data:application/pdf;base64,${pdfResult.base64}` : null,
          attachments: [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit authorization');
      }

      const resJson = await response.json();
      setCompletedAppId(resJson.submissionId || invite.id);
      setCompleted(true);
      toast.success('Credit card authorization submitted successfully!');
      if (onSubmitted) onSubmitted();
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit authorization — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
        <div className="text-center space-y-4">
          <Loader2 className="animate-spin text-indigo-600 mx-auto" size={48} />
          <p className="text-slate-500 dark:text-slate-400 font-medium">Loading your form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors duration-300">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 p-12 rounded-[3rem] shadow-xl border border-slate-100 dark:border-slate-800 text-center space-y-6">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle size={40} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Oops!</h2>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  // ── Completion screen ────────────────────────────────────────────────────
  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors duration-300">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 p-12 rounded-[3rem] shadow-xl border border-slate-100 dark:border-slate-800 text-center space-y-6">
          <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={40} />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Authorization Submitted!</h2>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
            Your credit card authorization has been received. Armellini Logistics will not charge your card without this
            signed authorization on file.
          </p>
          {invite && (
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{invite.companyName}</p>
          )}
          {pdfBlobUrl ? (
            <a
              href={pdfBlobUrl}
              download={`CreditCardAuth_${(invite?.companyName || 'Authorization').replace(/\s+/g, '_')}.pdf`}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-indigo-100 dark:shadow-indigo-900/20"
            >
              <FileText size={18} /> Download Copy (PDF)
            </a>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 px-8 py-4 rounded-2xl font-bold cursor-not-allowed"
            >
              <FileText size={18} /> PDF Unavailable
            </button>
          )}
          {completedAppId && (
            <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">Ref: {completedAppId}</p>
          )}
        </div>

        {/* Hidden PDF template — rendered off-screen for capture */}
        <div
          style={{ position: 'fixed', top: 0, left: 0, zIndex: -50, opacity: 0, pointerEvents: 'none', overflow: 'hidden', width: '1px', height: '1px' }}
        >
          <PdfTemplate formData={formData} invite={invite} completedAppId={completedAppId} pdfRef={pdfRef} />
        </div>
      </div>
    );
  }

  const STEPS = ['Card Information', 'Authorization & Signature'];
  const cardHolderFullName = [formData.card.cardHolderFirstName, formData.card.cardHolderLastName].filter(Boolean).join(' ');

  // ── Main form ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 transition-colors duration-300">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-12 relative">
          <div className="absolute right-0 top-0">
            <ThemeToggle />
          </div>
          <img
            src="https://firebasestorage.googleapis.com/v0/b/dataseeker-12378.firebasestorage.app/o/template-images%2F1770677341197_logo.png?alt=media&token=34c46eaa-ef71-403d-99e0-029e3462db45"
            alt="Armellini Logistics"
            className="h-12 mx-auto mb-8 dark:invert transition-all"
          />
          <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">Credit Card Authorization</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Armellini Logistics — Secure Card Authorization Form</p>
        </header>

        {/* Progress bar */}
        <div className="flex justify-between items-center mb-12 bg-white dark:bg-slate-900 p-4 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 transition-colors duration-300">
          {STEPS.map((label, idx) => {
            const s = idx + 1;
            return (
              <div key={s} className="flex items-center gap-2 shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  step >= s ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100 dark:shadow-indigo-900/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600'
                }`}>
                  {step > s ? <CheckCircle size={20} /> : s}
                </div>
                <span className={`hidden md:inline text-xs font-bold uppercase tracking-widest ${step >= s ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-600'}`}>
                  {label}
                </span>
                {s < STEPS.length && <div className="hidden md:block w-8 h-px bg-slate-200 dark:bg-slate-800 mx-2" />}
              </div>
            );
          })}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 transition-colors duration-300">
          <div className="p-12">

            {/* ── Step 1: Card Information ── */}
            {step === 1 && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-3">
                  <CreditCard className="text-indigo-600 dark:text-indigo-400" /> Card Information
                </h2>

                {/* Card holder name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Card Holder First Name"
                    value={formData.card.cardHolderFirstName}
                    error={errors.cardHolderFirstName}
                    onBlur={() => handleFieldBlur('cardHolderFirstName')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, cardHolderFirstName: v } }))}
                  />
                  <FormInput
                    label="Card Holder Last Name"
                    value={formData.card.cardHolderLastName}
                    error={errors.cardHolderLastName}
                    onBlur={() => handleFieldBlur('cardHolderLastName')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, cardHolderLastName: v } }))}
                  />
                </div>

                <FormInput
                  label="Company Name"
                  value={formData.card.companyName}
                  error={errors.companyName}
                  onBlur={() => handleFieldBlur('companyName')}
                  onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, companyName: v } }))}
                />

                {/* Card type */}
                <div className="space-y-3">
                  <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Card Type</label>
                  <div className="flex flex-wrap gap-3">
                    {CARD_TYPES.map(ct => (
                      <button
                        key={ct.value}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, card: { ...prev.card, cardType: ct.value } }));
                          handleFieldBlur('cardType');
                        }}
                        className={`px-5 py-3 rounded-2xl border-2 font-bold text-sm transition-all ${
                          formData.card.cardType === ct.value
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                            : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        {ct.label}
                      </button>
                    ))}
                  </div>
                  {errors.cardType && <p className="text-[10px] font-bold text-red-500 ml-1">{errors.cardType}</p>}
                </div>

                {/* Card number + expiry */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormInput
                    label="Credit Card Number"
                    value={formData.card.cardNumber}
                    error={errors.cardNumber}
                    onBlur={() => handleFieldBlur('cardNumber')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, cardNumber: v.replace(/\D/g, '') } }))}
                    className="md:col-span-1"
                  />
                  <FormInput
                    label="Exp Month (MM)"
                    value={formData.card.expMonth}
                    error={errors.expMonth}
                    onBlur={() => handleFieldBlur('expMonth')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, expMonth: v.replace(/\D/g, '').slice(0, 2) } }))}
                  />
                  <FormInput
                    label="Exp Year (YYYY)"
                    value={formData.card.expYear}
                    error={errors.expYear}
                    onBlur={() => handleFieldBlur('expYear')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, expYear: v.replace(/\D/g, '').slice(0, 4) } }))}
                  />
                </div>

                {/* CVV + phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="CVV (3 or 4 digits)"
                    value={formData.card.cvv}
                    error={errors.cvv}
                    onBlur={() => handleFieldBlur('cvv')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, cvv: v.replace(/\D/g, '').slice(0, 4) } }))}
                  />
                  <FormInput
                    label="Telephone"
                    value={formData.card.telephone}
                    error={errors.telephone}
                    onBlur={() => handleFieldBlur('telephone')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, telephone: v } }))}
                  />
                </div>

                {/* Billing address */}
                <FormInput
                  label="Credit Card Billing Address"
                  value={formData.card.billingAddress}
                  error={errors.billingAddress}
                  onBlur={() => handleFieldBlur('billingAddress')}
                  onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, billingAddress: v } }))}
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <FormInput
                    label="City"
                    value={formData.card.city}
                    error={errors.city}
                    onBlur={() => handleFieldBlur('city')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, city: v } }))}
                    className="col-span-2"
                  />
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">State</label>
                    <select
                      value={formData.card.state}
                      onChange={e => setFormData(prev => ({ ...prev, card: { ...prev.card, state: e.target.value } }))}
                      onBlur={() => handleFieldBlur('state')}
                      className={`w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none ${
                        errors.state ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800'
                      }`}
                    >
                      <option value="">ST</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.state && <p className="text-[10px] font-bold text-red-500 ml-1">{errors.state}</p>}
                  </div>
                  <FormInput
                    label="ZIP Code"
                    value={formData.card.zipCode}
                    error={errors.zipCode}
                    onBlur={() => handleFieldBlur('zipCode')}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, zipCode: v } }))}
                  />
                </div>

                {/* Invoice + total */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Invoice / Order # (optional)"
                    value={formData.card.invoiceOrderNumber}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, invoiceOrderNumber: v } }))}
                  />
                  <FormInput
                    label="Total Amount $ (optional)"
                    value={formData.card.totalAmount}
                    onChange={v => setFormData(prev => ({ ...prev, card: { ...prev.card, totalAmount: v } }))}
                  />
                </div>
              </div>
            )}

            {/* ── Step 2: Authorization & Signature ── */}
            {step === 2 && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-3">
                  <ShieldCheck className="text-indigo-600 dark:text-indigo-400" /> Authorization & Signature
                </h2>

                {/* Authorization text */}
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 border border-slate-100 dark:border-slate-700 space-y-3">
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                    I, <span className="font-bold text-slate-900 dark:text-slate-100">{cardHolderFullName || '_______________'}</span>, hereby authorize Armellini Logistics to charge my credit card. As the Credit Card holder, I also authorize Armellini Logistics to charge my credit card for future purchases up until the date stated below.
                  </p>
                </div>

                {/* Authorization valid until + initials */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormInput
                    label="Valid Until Month (MM)"
                    value={formData.authorization.validUntilMonth}
                    error={errors.validUntilMonth}
                    onBlur={() => handleFieldBlur('validUntilMonth')}
                    onChange={v => setFormData(prev => ({ ...prev, authorization: { ...prev.authorization, validUntilMonth: v.replace(/\D/g, '').slice(0, 2) } }))}
                  />
                  <FormInput
                    label="Valid Until Year (YYYY)"
                    value={formData.authorization.validUntilYear}
                    error={errors.validUntilYear}
                    onBlur={() => handleFieldBlur('validUntilYear')}
                    onChange={v => setFormData(prev => ({ ...prev, authorization: { ...prev.authorization, validUntilYear: v.replace(/\D/g, '').slice(0, 4) } }))}
                  />
                  <FormInput
                    label="Initials"
                    value={formData.authorization.initials}
                    error={errors.initials}
                    onBlur={() => handleFieldBlur('initials')}
                    onChange={v => setFormData(prev => ({ ...prev, authorization: { ...prev.authorization, initials: v.slice(0, 6) } }))}
                  />
                </div>

                {/* Policy acceptance */}
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-2xl p-6 border border-amber-100 dark:border-amber-900/30 space-y-4">
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                    I understand that I will <strong>NOT</strong> receive a CASH refund. I also agree to follow all credit policies and requirements as stated on all of my purchased invoices; we are not responsible for any carrier related problems.
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    There is a <strong>3% Convenience fee</strong> on all credit cards.
                  </p>
                </div>

                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.authorization.policyAccepted}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        authorization: { ...prev.authorization, policyAccepted: e.target.checked },
                      }));
                      handleFieldBlur('policyAccepted');
                    }}
                    className="w-5 h-5 mt-0.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 shrink-0"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                    I have read and agree to the above authorization terms. I understand this is a legally binding authorization.
                  </span>
                </label>
                {errors.policyAccepted && (
                  <p className="text-[10px] font-bold text-red-500 ml-1">{errors.policyAccepted}</p>
                )}

                {/* Signature */}
                <div className="space-y-3">
                  <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Authorized Signature</label>
                  <SignaturePad
                    label="Authorized Signature"
                    onSave={sig => {
                      setFormData(prev => ({
                        ...prev,
                        authorization: { ...prev.authorization, signature: sig },
                      }));
                      handleFieldBlur('signature');
                    }}
                  />
                  {errors.signature && (
                    <p className="text-[10px] font-bold text-red-500 ml-1">{errors.signature}</p>
                  )}
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Date</label>
                  <div className="px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border border-slate-100 dark:border-slate-800 text-sm font-medium">
                    {formData.authorization.signedDate}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-12 pt-8 border-t border-slate-100 dark:border-slate-800">
              {step > 1 ? (
                <button
                  onClick={handlePrev}
                  disabled={submitting}
                  className="flex items-center gap-2 px-8 py-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-bold transition-all disabled:opacity-50"
                >
                  <ChevronLeft size={20} /> Previous
                </button>
              ) : (
                <div />
              )}

              {step === 1 && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-indigo-100 dark:shadow-indigo-900/20"
                >
                  Next <ChevronRight size={20} />
                </button>
              )}

              {step === 2 && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-white px-12 py-4 rounded-2xl font-bold transition-all shadow-xl disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                  Submit Authorization
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PDF Template ─────────────────────────────────────────────────────────────
// Rendered off-screen and captured by html2canvas to produce the downloadable PDF.
// Layout mirrors the original Armellini Logistics credit card authorization form.

interface PdfTemplateProps {
  formData: FormState;
  invite: Invite | null;
  completedAppId: string;
  pdfRef: React.RefObject<HTMLDivElement>;
}

function PdfTemplate({ formData, invite, completedAppId, pdfRef }: PdfTemplateProps) {
  const cardHolderFullName = [formData.card.cardHolderFirstName, formData.card.cardHolderLastName].filter(Boolean).join(' ');
  const cardTypeLabelMap: Record<string, string> = {
    visa: 'VISA', mastercard: 'MASTERCARD', amex: 'AMERICAN EXPRESS', discover: 'DISCOVER', debit: 'DEBIT CARD',
  };

  return (
    <div
      ref={pdfRef}
      style={{
        width: '794px',
        minHeight: '1123px',
        padding: '64px 72px',
        backgroundColor: '#ffffff',
        color: '#0f172a',
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        lineHeight: '1.6',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.15em', color: '#475569' }}>STELLAR</span>
          <span style={{ fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.15em', color: '#475569' }}>BOUQUET</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0', letterSpacing: '0.05em' }}>
            CREDIT CARD AUTHORIZATION FORM
          </h1>
        </div>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/dataseeker-12378.firebasestorage.app/o/template-images%2F1770677341197_logo.png?alt=media&token=34c46eaa-ef71-403d-99e0-029e3462db45"
          alt="Armellini Logistics"
          style={{ height: '36px' }}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      </div>

      {/* Authorization paragraph */}
      <p style={{ fontWeight: 'bold', fontSize: '14px', margin: '0', lineHeight: '1.7' }}>
        I, <u>{cardHolderFullName}</u>, hereby authorize Armellini Logistics to charge my credit card. As the
        Credit Card holder; I also authorize Armellini Logistics to charge my credit card for future
        purchases up until the date stated below.
      </p>

      {/* Auth valid until + initials */}
      <div style={{ display: 'flex', gap: '48px', fontWeight: 'bold', fontSize: '13px' }}>
        <span>
          Authorization Valid Until:{' '}
          <u style={{ minWidth: '80px', display: 'inline-block', textAlign: 'center' }}>
            {formData.authorization.validUntilMonth || '__'}/{formData.authorization.validUntilYear || '____'}
          </u>
        </span>
        <span>
          Initials here:{' '}
          <u style={{ minWidth: '60px', display: 'inline-block', textAlign: 'center' }}>
            {formData.authorization.initials || '_______'}
          </u>
        </span>
      </div>

      {/* Policy text */}
      <p style={{ fontWeight: 'bold', fontSize: '13px', margin: '0', lineHeight: '1.7' }}>
        I understand that I will NOT receive a CASH refund. I also agree to follow all credit policies and
        requirements as stated on all of my purchased invoices; we are not responsible for any carrier related
        problems.
      </p>

      {/* Card types */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '32px' }}>
        {CARD_TYPES.map(ct => (
          <div key={ct.value} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', fontSize: '13px' }}>
            <span style={{
              display: 'inline-block', width: '14px', height: '14px',
              border: '2px solid #374151', borderRadius: '2px',
              backgroundColor: formData.card.cardType === ct.value ? '#374151' : '#ffffff',
              flexShrink: 0,
            }} />
            {ct.label}
          </div>
        ))}
      </div>

      {/* Card details fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', fontWeight: 'bold' }}>
        <div>
          Company Name:{' '}
          <u style={{ letterSpacing: '0.05em' }}>{formData.card.companyName}</u>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            Credit Card #: <u>{formData.card.cardNumber}</u>
          </span>
          <span>
            Exp: <u>{formData.card.expMonth}/{formData.card.expYear}</u>
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            CVV # (3 or 4 digits): <u>{formData.card.cvv}</u>
          </span>
          <span>
            Telephone: <u>{formData.card.telephone}</u>
          </span>
        </div>
        <div>
          Credit Card Billing Address: <u>{formData.card.billingAddress}</u>
        </div>
        <div style={{ display: 'flex', gap: '32px' }}>
          <span>City: <u>{formData.card.city}</u></span>
          <span>ST: <u>{formData.card.state}</u></span>
          <span>ZipCode: <u>{formData.card.zipCode}</u></span>
        </div>
        {formData.card.invoiceOrderNumber && (
          <div>Invoice / Order #: <u>{formData.card.invoiceOrderNumber}</u></div>
        )}
        {formData.card.totalAmount && (
          <div>Total $: <u>{formData.card.totalAmount}</u></div>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #cbd5e1', marginTop: '8px' }} />

      {/* Signature section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, borderBottom: '1px solid #374151', paddingBottom: '4px', textAlign: 'center', fontSize: '12px', color: '#374151' }}>
            <div style={{ fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>{formData.card.cardHolderFirstName}</div>
            Print Card Holder Name
          </div>
          <div style={{ flex: 1, borderBottom: '1px solid #374151', paddingBottom: '4px', textAlign: 'center', fontSize: '12px', color: '#374151' }}>
            <div style={{ fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>{formData.card.cardHolderLastName}</div>
            Last Name
          </div>
          <div style={{ width: '140px', borderBottom: '1px solid #374151', paddingBottom: '4px', textAlign: 'center', fontSize: '12px', color: '#374151' }}>
            <div style={{ fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>{formData.authorization.signedDate}</div>
            Date
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px' }}>
          <span style={{ fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap' }}>Authorized Signature X</span>
          <div style={{ flex: 1, borderBottom: '1px solid #374151', minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            {renderSignatureForPdf(formData.authorization.signature)}
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Disclaimer */}
      <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0', fontSize: '11px', color: '#475569', lineHeight: '1.6' }}>
        <p style={{ margin: '0 0 8px 0' }}>
          Please read carefully and complete the entire form — this will help protect against fraud. Armellini Logistics will
          not charge your Credit Card without the proper authorized written approval. There is a{' '}
          <strong>3% Convenience fee</strong> on all credit cards.
        </p>
        <p style={{ margin: '0', color: '#0284c7' }}>
          Complete and sign this form, email back to: Adubner@armellini.com
        </p>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '16px', fontSize: '11px', color: '#64748b' }}>
        <p style={{ margin: '0' }}>
          P.O.BOX 520646 Miami, Florida 33152 — Phone: (786) 547-9894 — Adubner@armellini.com
        </p>
        {completedAppId && (
          <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace', fontSize: '10px', color: '#94a3b8' }}>
            Ref: {completedAppId}
          </p>
        )}
      </div>
    </div>
  );
}
